import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { NavigationService } from '@treasury/core/navigation';
import { Account, DashboardService } from '@treasury/domain/dashboard';
import { Feature, FeatureFlagService } from '@treasury/domain/services/feature-flags';
import { TmBaseComponent } from '@treasury/presentation';
import { chevronDownIcon, chevronUpIcon, settingsIcon } from '@treasury/presentation/assets/icons';
import '@treasury/presentation/components/tm-card';
import '@treasury/presentation/components/tm-fdic-digital-sign';
import '@vaadin/grid';
import {
    GridColumnBodyLitRenderer,
    columnBodyRenderer,
    gridRowDetailsRenderer,
} from '@vaadin/grid/lit.js';
import '@vaadin/grid/vaadin-grid-column.js';
import '@vaadin/grid/vaadin-grid-sort-column.js';
import '@vaadin/item';
import { css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { AccountViewModel } from '../data/account-view-model';
import './configure-accounts/available-accounts-sheet';
import './configure-accounts/configure-account-groups-sheet';

export const tagName = 'accounts-widget';
@customElement(tagName)
export default class AccountsWidget extends TmBaseComponent {
    @InjectProperty()
    declare private navService: NavigationService;

    @state()
    private accountGroups: { name: string; accounts: AccountViewModel[] }[] = [];

    @InjectProperty()
    declare private dashboardService: DashboardService;

    @InjectProperty({
        async: true,
        token: FeatureFlagService,
    })
    declare private featureFlagService: Promise<FeatureFlagService>;

    @state()
    private activeAccounts: AccountViewModel[] = [];

    @state()
    private selectedAccounts: AccountViewModel[] = [];

    @state()
    private selectedAccountGroup: { name: string; accounts: AccountViewModel[] }[] = [];

    @state()
    private loading = true;

    @state()
    private filteredAccounts: AccountViewModel[] = [];

    @state()
    private hasManageAccounts = false;

    @state()
    private manageAccountsSheetOpen = false;

    @state()
    private addAccountsSheetOpen = false;

    @state()
    private accountGroupName = '';

    @state()
    private hideGroupBalance = false;

    protected render() {
        return html`<tm-card .header=${this.renderHeader()} fullwidth>
                ${this.renderContent()} </tm-card
            ><configure-account-groups-sheet
                .open=${this.manageAccountsSheetOpen}
                @showAccountSelection=${(e: CustomEvent) => {
                    this.accountGroupName = e.detail;
                    this.addAccountsSheetOpen = true;
                }}
                @close=${() => {
                    this.manageAccountsSheetOpen = false;
                    this.firstUpdated();
                }}
            ></configure-account-groups-sheet>`;
    }

    protected async firstUpdated() {
        this.loading = true;
        const groups = (await this.dashboardService.getMyAccounts()) as {
            name: string;
            accounts: Account[];
        }[];

        this.accountGroups = groups.map(group => ({
            name: group.name,
            accounts: group.accounts.map(account => new AccountViewModel(account)),
        }));
        this.selectedAccountGroup = [this.accountGroups[0]];
        this.activeAccounts = this.filteredAccounts;
        this.hideGroupBalance = await (
            await this.featureFlagService
        ).isEnabled(Feature.HideAccountsWidgetGroupBalance);
        this.hasManageAccounts = await (
            await this.featureFlagService
        ).isEnabled(Feature.PwaDashboardManageAccounts);
        this.loading = false;
    }

    private get userHasSetUpAccounts() {
        return this.accountGroups.some(
            (accountGroup: { name: string; accounts: AccountViewModel[] }) =>
                accountGroup.accounts.length > 0
        );
    }

    private accountClicked(account: AccountViewModel) {
        this.selectedAccounts = account ? [account] : [];
        const [selectedAccount] = this.selectedAccounts;
        if (selectedAccount) {
            this.navService.navigate(`/accounts/${selectedAccount.type}/${selectedAccount.id}`);
        }
    }

    private accountGroupClicked(accountGroup: { name: string; accounts: AccountViewModel[] }) {
        this.selectedAccountGroup = accountGroup ? [accountGroup] : [];
    }

    private renderNameAndBalance(account: AccountViewModel) {
        return html`<div class="flex justify-between pr-6">
            <div class="flex flex-col">
                <span class="text-sm font-medium">${account.name}</span>
                <small class="text-xs">${account.number}</small>
            </div>
            <div class="flex flex-col amount-and-status-col">
                <span class="text-sm font-medium">${account.visibleBalance}</span>
                <small class="text-xs">${account.visibleBalanceLabel}</small>
            </div>
        </div>`;
    }

    private renderNameAndNumber(account: AccountViewModel) {
        return html` <div class="flex flex-col">
            <span class="text-sm font-medium">${account.name}</span>
            <small class="text-xs">${account.number}</small>
        </div>`;
    }

    private renderGroupAccounts(accountGroup: { name: string; accounts: Account[] }) {
        return html`<vaadin-grid
                .allRowsVisible=${accountGroup.accounts.length <= 12}
                .items=${accountGroup.accounts}
                .selectedItems=${this.selectedAccounts}
                @active-item-changed=${({ detail }: CustomEvent) =>
                    this.accountClicked(detail.value)}
                .hidden=${screen.width >= 1025}
            >
                <vaadin-grid-column
                    ${columnBodyRenderer(
                        this.renderNameAndBalance as GridColumnBodyLitRenderer<AccountViewModel>
                    )}
                ></vaadin-grid-column>
            </vaadin-grid>
            <vaadin-grid
                id="accounts-grid"
                .allRowsVisible=${accountGroup.accounts.length <= 12}
                .items=${accountGroup.accounts}
                .selectedItems=${this.selectedAccounts}
                @active-item-changed=${({ detail }: CustomEvent) =>
                    this.accountClicked(detail.value)}
                .hidden=${screen.width < 1025}
            >
                <vaadin-grid-column
                    ${columnBodyRenderer(
                        this.renderNameAndNumber as GridColumnBodyLitRenderer<AccountViewModel>
                    )}
                ></vaadin-grid-column>
                <vaadin-grid-sort-column
                    path="currentBalanceFormatted"
                    header="Current Balance"
                    flex-grow="1"
                    width="160px"
                ></vaadin-grid-sort-column>
                <vaadin-grid-sort-column
                    path="availableBalanceFormatted"
                    header="Available Balance"
                    flex-grow="1"
                    width="160px"
                ></vaadin-grid-sort-column>
                <vaadin-grid-sort-column
                    path="collectedBalanceFormatted"
                    header="Collected Balance"
                    flex-grow="1"
                    width="160px"
                ></vaadin-grid-sort-column
            ></vaadin-grid>`;
    }

    private renderGroupNameAndCount(accountGroup: { name: string; accounts: AccountViewModel[] }) {
        return html`<div class="py-1.5 text-sm">
            <span class="font-medium group-name">${accountGroup.name}&nbsp;</span
            ><span class="account-count">(${accountGroup.accounts.length})</span>
        </div>`;
    }

    private renderGroupAmount(accountGroup: { name: string; accounts: AccountViewModel[] }) {
        if (!accountGroup.accounts.length) {
            return html``;
        }
        const availableBalances = accountGroup.accounts.map(
            (acc: AccountViewModel) => acc.availableBalance
        );
        const totalAvailableBalance = availableBalances.reduce((a, b) => a + b, 0);
        const totalAvailableBalanceFormatted = formatCurrency(totalAvailableBalance);
        const chevron =
            accountGroup.name === this.selectedAccountGroup[0]?.name
                ? chevronUpIcon
                : chevronDownIcon;

        return html`<div class="flex flex-shrink-0 justify-end items-center py-1.5">
            <div class="flex flex-col">
                ${this.hideGroupBalance
                    ? nothing
                    : html`<span class="text-sm font-medium total-amount"
                              >${totalAvailableBalanceFormatted}</span
                          ><span class="balance-type text-xs">Group Available</span>`}
            </div>
            <span class="group-toggle">${chevron}</span>
        </div>`;
    }

    private renderTable() {
        if (this.loading) {
            return html`<tm-loader card white></tm-loader>`;
        }
        return html`
            <vaadin-grid
                id="account-groups-grid"
                class="border-b"
                all-rows-visible
                .items=${this.accountGroups}
                .detailsOpenedItems=${this.selectedAccountGroup}
                @active-item-changed=${({ detail }: CustomEvent) =>
                    this.accountGroupClicked(detail.value)}
                ${gridRowDetailsRenderer<{ name: string; accounts: Account[] }>(
                    this.renderGroupAccounts
                )}
            >
                <vaadin-grid-column
                    ${columnBodyRenderer(
                        this.renderGroupNameAndCount as GridColumnBodyLitRenderer<{
                            name: string;
                            accounts: AccountViewModel[];
                        }>
                    )}
                ></vaadin-grid-column>
                <vaadin-grid-column
                    text-align="end"
                    ${columnBodyRenderer(
                        this.renderGroupAmount as GridColumnBodyLitRenderer<{
                            name: string;
                            accounts: AccountViewModel[];
                        }>
                    )}
                ></vaadin-grid-column>
            </vaadin-grid>
        `;
    }

    private renderGroupSelectionItems() {
        return this.accountGroups.map(
            (accountGroup: { name: string; accounts: AccountViewModel[] }) =>
                html`<vaadin-item>${accountGroup.name || 'All'}</vaadin-item>`
        );
    }

    private renderContent() {
        if (this.loading) {
            return html`<tm-loader card></tm-loader>`;
        }
        return this.userHasSetUpAccounts
            ? this.renderTable()
            : html`<p class="text-center text-sm">No account groups available</p>`;
    }

    private renderHeader() {
        return html`<div class="flex justify-between items-center">
                <div>
                    <div class="font-medium text-header">Accounts</div>
                    ${this.userHasSetUpAccounts
                        ? html`<div class="font-normal text-xs text-secondary">
                              All Account Groups
                          </div>`
                        : nothing}
                </div>
                ${this.hasManageAccounts
                    ? html` <tm-button
                          importance="tertiary"
                          @click=${() => {
                              this.manageAccountsSheetOpen = true;
                          }}
                          ><tm-icon .icon=${settingsIcon}></tm-icon
                      ></tm-button>`
                    : nothing}
            </div>
            ${this.userHasSetUpAccounts
                ? html`<div class="pt-2">
                      <tm-fdic-digital-sign></tm-fdic-digital-sign>
                  </div>`
                : nothing}`;
    }

    public static get styles() {
        return [
            css`
                :host {
                    flex: 1;
                    --tm-card-min-width: auto;
                    --tm-card-min-height: auto;
                    --grouped-table-row-color: var(--jh-color-gray-50);
                }
                :host([theme='dark']) {
                    --grouped-table-row-color: var(--jh-color-gray-950);
                }
                svg {
                    display: inline;
                }
                small {
                    color: var(--secondary-text-color);
                    margin-left: 0;
                }
                span {
                    color: var(--primary-text-color);
                }
                vaadin-grid-cell-content {
                    padding: 0;
                }
                vaadin-grid::part(cell) {
                    display: flex;
                    flex-direction: column;
                    justify-items: flex-start;
                    padding-top: 0.375rem; /* 6px */
                    padding-bottom: 0.375rem;
                    font-size: 0.875rem;
                    line-height: 1.25rem;
                }
                vaadin-grid::part(first-column-cell) {
                    padding-left: 16px;
                }
                vaadin-grid::part(last-column-cell) {
                    padding-right: 6px;
                }
                #account-groups-grid::part(cell) {
                    background-color: var(--grouped-table-row-color);
                }
                #accounts-grid::part(last-column-cell) {
                    padding-right: 25px;
                }
                #show-group {
                    color: var(--primary-text-color);
                }
                .amount-and-status-col {
                    text-align: right;
                }
                path {
                    fill: var(--secondary-text-color);
                }
                .group-toggle svg {
                    display: inline-block;
                }
                .group-name,
                .total-amount {
                    color: var(--primary-text-color);
                }
                .account-count,
                .balance-type {
                    color: var(--secondary-text-color);
                }
            `,
        ];
    }
}

function formatCurrency(amount: number) {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    }).format(amount);
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: AccountsWidget;
    }
}
